/**
 * Browser-side API client. Calls the BFF (/api/*), which holds the sk_
 * server-side. Types mirror the auth-server's response shapes.
 */

export interface DailyVolumePoint {
  /** UTC calendar day, `YYYY-MM-DD`. */
  date: string;
  total: number;
  successful: number;
  failed: number;
}

export interface KpiDeltas {
  authVolume: number;
  successRate: number;
  enrolledUsers: number;
}

export interface AnalyticsResult {
  totalVerifications: number;
  successCount: number;
  failureCount: number;
  totalEnrollments: number;
  activePersonas: number;
  /** Last `windowDays` of daily auth volume, zero-filled (drives the area chart). */
  series: DailyVolumePoint[];
  /** Period-over-period fractional change for the KPI cards. */
  deltas: KpiDeltas;
  windowDays: number;
  /** Distinct provider-mapped personas by type. */
  personaMix: { human: number; agent: number };
  liveness: LivenessStats;
  liveness24h: LivenessStats;
}
export interface LivenessStats { trials: number; passed: number; failed: number; topFailureReason: string | null }

export interface AuditEventRow {
  id: string;
  providerId: string;
  /** The provider's own user id for the event's persona, if mapped. */
  externalUserId: string | null;
  /** The opaque per-provider subject (JWT `sub`), if minted. */
  providerSubject: string | null;
  action: string;
  severity: "security" | "operational";
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface ApiKeyRow {
  id: string;
  providerId: string;
  keyPrefix: string;
  label: string | null;
  status: string;
  expiresAt: string | null;
  createdAt: string;
}

export interface ApiKeyCreateResult {
  apiKey: string;
  keyId: string;
}

export interface ProviderInfo {
  id: string;
  name: string;
  rateLimitMax: number;
  status: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProviderResult {
  provider: ProviderInfo;
}

/**
 * Server-persisted onboarding state. See `server/db/provider-extras.ts` for
 * the canonical definition; this mirror is the SPA's view of it.
 *
 * `step` drives the wizard's "which screen am I on" routing. `completedAt`
 * and `skippedAt` are mutually exclusive terminal flags — either non-null
 * short-circuits the guard and routes the operator to `/`.
 */
export type OnboardingStep = "survey" | "explainer" | "generate_key" | "done";

export interface OnboardingSurvey {
  appType?: "web_app" | "mobile_app" | "api_service" | "ai_agent";
  authMethods?: Array<"passkeys" | "image_points" | "both">;
  monthlyVolume?: "lt_1k" | "1k_10k" | "gt_10k";
}

export interface OnboardingState {
  step: OnboardingStep;
  survey?: OnboardingSurvey;
  completedAt?: string;
  skippedAt?: string;
}

export interface DashboardUserRow {
  externalUserId: string;
  providerSubject: string | null;
  personaType: string;
  personaStatus: string;
  enrolledFactors: number;
  authCount: number;
  lastAuthAt: string | null;
  createdAt: string;
}

export interface UserStats {
  enrolled: number;
  active7d: number;
  new7d: number;
  agentPersonas: number;
}

export interface UsageSummary {
  authVolume: { total: number; successful: number; failed: number };
  enrolledPersonas: number;
}

export interface BillingSummary {
  providerId: string;
  plan: {
    name: string;
    monthlyLimit: number;
    pricePerMonth: number;
    features: string[];
  };
  usage: UsageSummary;
  billingPeriod: { start: string; end: string };
}

export interface HealthInfo {
  connected: boolean;
  serverUrl: string;
  error?: string;
}

export type TeamRole = "owner" | "admin" | "viewer";

export interface TeamMemberRow {
  userId: string;
  email: string;
  name: string;
  role: TeamRole;
  status: "active";
  createdAt: string;
  lastSeen: string | null;
}

export interface TeamInvitationRow {
  id: string;
  email: string;
  role: TeamRole;
  createdAt: string;
  expiresAt: string;
  invitedByEmail: string | null;
}

export interface MyProviderRow {
  id: string;
  name: string;
  role: TeamRole;
}

/**
 * Aggregate state for the Quickstart screen. Server batches the underlying
 * queries; the SPA renders progress dots + linked CTAs off this single
 * response. Mirrors `server/queries/quickstart.ts::QuickstartState`.
 */
export interface QuickstartState {
  providerName: string;
  apiKeys: {
    activeCount: number;
    latestKeyPrefix: string | null;
    latestCreatedAt: string | null;
  };
  firstAuth: {
    hasAuth: boolean;
    latestAuthAt: string | null;
    latestExternalUserId: string | null;
  };
  team: {
    activeMembers: number;
    pendingInvitations: number;
  };
  /** True once the provider set a co-brand display name on /branding. */
  brandingConfigured: boolean;
  onboardingCompletedAt: string | null;
}

export type FactorCombination =
  | "passkey_and_image_points"
  | "passkey_only"
  | "passkey_and_behavior";
export type AgentPolicy = "allowed" | "blocked";

export interface ProviderPolicy {
  factorCombination: FactorCombination;
  agentPolicy: AgentPolicy;
  agentRateLimitPerMin: number;
}

// Auth gates — extra requirements layered on top of any policy. Orthogonal to
// ProviderPolicy (own column/route). Mirrors the auth-server's LivenessMode.
import type { LivenessMode } from "./liveness";
export type { LivenessMode, LivenessTrialMeta } from "./liveness";
export interface LivenessAvailability {
  /** Deployment ceiling: strongest mode publishable in this environment. */
  maxMode: LivenessMode;
  /** Combinations for which enforce is publishable (ceremony collects the attestation). */
  enforceReadyCombinations: FactorCombination[];
}
export interface AuthGates {
  liveness: { mode: LivenessMode };
  availability: LivenessAvailability;
}

export type Font =
  | "Inter"
  | "Space Grotesk"
  | "IBM Plex Sans"
  | "Geist"
  | "System UI";
export type ButtonStyle = "square" | "rounded" | "pill";
export type Mode = "light" | "dark" | "auto";

export interface ProviderBranding {
  logoText: string;
  logoUrl: string | null;
  primary: string;
  accent: string;
  font: Font;
  buttonStyle: ButtonStyle;
  mode: Mode;
  copyTitle: string;
  copyDesc: string;
  copyCta: string;
}

export interface ProviderHosted {
  allowedCallbackOrigins: string[];
}

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Only advertise a JSON body when we're actually sending one. Fastify rejects
  // bodyless requests (e.g. DELETE) that carry `Content-Type: application/json`
  // with "Body cannot be empty when content-type is set to 'application/json'".
  const headers = new Headers(init.headers);
  if (init.body != null && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(path, { ...init, headers });
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, msg);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const apiClient = {
  health: () => api<HealthInfo>("/api/health"),

  provider: {
    get: () => api<ProviderInfo>("/api/provider"),
    update: (data: { name?: string; rateLimitMax?: number }) =>
      api<ProviderInfo>("/api/provider", {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    /** Owner-only soft-delete; `confirmName` must echo the org's exact name. */
    remove: (confirmName: string) =>
      api<void>("/api/provider", {
        method: "DELETE",
        body: JSON.stringify({ confirmName }),
      }),
  },

  providers: {
    /**
     * Self-serve "Create organization". Returns the freshly-minted provider.
     * No api key is minted here anymore — the onboarding wizard's
     * "Generate first API key" step calls `/api/api-keys` separately, and
     * the operator can also skip that step and mint one later from Keys.
     */
    create: (data: { name: string }) =>
      api<CreateProviderResult>("/api/providers", {
        method: "POST",
        body: JSON.stringify(data),
      }),
  },

  onboarding: {
    /** Read the current provider's onboarding state. Defaults if untouched. */
    get: () => api<OnboardingState>("/api/onboarding"),
    /**
     * Partial update. The server merges shallowly: top-level keys overwrite,
     * `survey` keys merge in. Already-terminal state (`completedAt` or
     * `skippedAt` set) short-circuits — the PATCH is a no-op.
     */
    patch: (patch: Partial<OnboardingState>) =>
      api<OnboardingState>("/api/onboarding", {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
  },

  apiKeys: {
    list: () => api<ApiKeyRow[]>("/api/api-keys"),
    create: (label?: string) =>
      api<ApiKeyCreateResult>("/api/api-keys", {
        method: "POST",
        body: JSON.stringify({ label }),
      }),
    revoke: (keyId: string) =>
      api<void>(`/api/api-keys/${encodeURIComponent(keyId)}`, {
        method: "DELETE",
      }),
  },

  dashboard: {
    analytics: () => api<AnalyticsResult>("/api/dashboard/analytics"),
    userStats: () => api<UserStats>("/api/dashboard/user-stats"),
    users: (q?: { limit?: number; search?: string; cursor?: string }) => {
      const params = new URLSearchParams();
      if (q?.limit) params.set("limit", String(q.limit));
      if (q?.search) params.set("search", q.search);
      if (q?.cursor) params.set("cursor", q.cursor);
      const qs = params.toString();
      return api<DashboardUserRow[]>(
        `/api/dashboard/users${qs ? `?${qs}` : ""}`,
      );
    },
    user: (externalUserId: string) =>
      api<DashboardUserRow>(
        `/api/dashboard/users/${encodeURIComponent(externalUserId)}`,
      ),
    auditLog: (q?: {
      limit?: number;
      action?: string;
      externalUserId?: string;
      since?: string;
      until?: string;
      cursor?: string;
    }) => {
      const params = new URLSearchParams();
      if (q?.limit) params.set("limit", String(q.limit));
      if (q?.action) params.set("action", q.action);
      if (q?.externalUserId) params.set("externalUserId", q.externalUserId);
      if (q?.since) params.set("since", q.since);
      if (q?.until) params.set("until", q.until);
      if (q?.cursor) params.set("cursor", q.cursor);
      const qs = params.toString();
      return api<AuditEventRow[]>(
        `/api/dashboard/audit-log${qs ? `?${qs}` : ""}`,
      );
    },
  },

  billing: {
    summary: () => api<BillingSummary>("/api/billing"),
    usage: () => api<UsageSummary>("/api/billing/usage"),
  },

  policy: {
    get: () => api<ProviderPolicy>("/api/policy"),
    update: (patch: Partial<ProviderPolicy>) =>
      api<ProviderPolicy>("/api/policy", {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
  },

  authGates: {
    get: () => api<AuthGates>("/api/auth-gates"),
    update: (patch: { liveness?: { mode: LivenessMode } }) =>
      api<AuthGates>("/api/auth-gates", {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
  },

  branding: {
    get: () => api<ProviderBranding>("/api/branding"),
    update: (patch: Partial<ProviderBranding>) =>
      api<ProviderBranding>("/api/branding", {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
  },

  hosted: {
    get: () => api<ProviderHosted>("/api/hosted"),
    update: (patch: Partial<ProviderHosted>) =>
      api<ProviderHosted>("/api/hosted", {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
  },

  team: {
    members: () => api<TeamMemberRow[]>("/api/team/members"),
    invitations: () => api<TeamInvitationRow[]>("/api/team/invitations"),
    invite: (data: { email: string; role: TeamRole }) =>
      api<{ id: string; email: string; role: TeamRole; expiresAt: string }>(
        "/api/team/invitations",
        { method: "POST", body: JSON.stringify(data) },
      ),
    revokeInvitation: (id: string) =>
      api<void>(`/api/team/invitations/${encodeURIComponent(id)}`, {
        method: "DELETE",
      }),
    accept: (token: string) =>
      api<{ providerId: string; role: TeamRole }>(
        `/api/team/invitations/${encodeURIComponent(token)}/accept`,
        { method: "POST", body: JSON.stringify({}) },
      ),
    changeRole: (userId: string, role: TeamRole) =>
      api<{ userId: string; role: TeamRole }>(
        `/api/team/members/${encodeURIComponent(userId)}`,
        { method: "PATCH", body: JSON.stringify({ role }) },
      ),
    remove: (userId: string) =>
      api<void>(`/api/team/members/${encodeURIComponent(userId)}`, {
        method: "DELETE",
      }),
    switch: (providerId: string) =>
      api<{ providerId: string }>("/api/team/switch", {
        method: "POST",
        body: JSON.stringify({ providerId }),
      }),
    myProviders: () => api<MyProviderRow[]>("/api/team/my-providers"),
  },

  quickstart: {
    get: () => api<QuickstartState>("/api/quickstart"),
  },
};

export { ApiError };
